// @figma file=tbd node=tbd name="ChatMessage" set="code-factory" verified=tbd

import * as React from 'react';
import { cn } from '../../../lib/utils';
import { MessageBlockRenderer } from './blocks/message-block-renderer';
import type { ContentBlock } from './chat-content';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ChatMessageRole = 'user' | 'assistant' | 'system';

export interface ChatMessageProps extends React.HTMLAttributes<HTMLDivElement> {
  role: ChatMessageRole;
  /** Plain text content — used when blocks is not provided */
  content?: string;
  /** Rich content blocks — renders instead of content when provided */
  blocks?: ContentBlock[];
  /** Optional timestamp string (pre-formatted) */
  timestamp?: string;
  /** Avatar slot — shown left of assistant messages, right of user messages */
  avatar?: React.ReactNode;
  /** Show a loading/streaming indicator instead of content */
  loading?: boolean;
  /**
   * Controls avatar placement and the corresponding bubble corner shape:
   * - `'top'`    — avatar top-left, square top-left corner on bubble
   * - `'bottom'` — avatar bottom-left (aligned with timestamp), square bottom-left corner
   * - `'none'`   — avatar hidden even if provided
   * - `undefined` — default: avatar top-left, standard rounded-bl-sm tail
   */
  avatarPosition?: 'top' | 'bottom' | 'none';
}

// ─── Streaming dots ───────────────────────────────────────────────────────────

function StreamingDots() {
  return (
    <span className="inline-flex items-center gap-0.5" role="img" aria-label="Thinking">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="size-1.5 rounded-full bg-muted-foreground animate-bounce"
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </span>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** True when blocks contain at least one non-text type (needs more space) */
function hasComplexBlocks(blocks: ContentBlock[]) {
  return blocks.some((b) => b.type !== 'text');
}

// ─── Component ────────────────────────────────────────────────────────────────

const ChatMessage = React.forwardRef<HTMLDivElement, ChatMessageProps>(
  (
    {
      role,
      content,
      blocks,
      timestamp,
      avatar,
      loading = false,
      avatarPosition,
      className,
      ...props
    },
    ref,
  ) => {
    const isUser = role === 'user';
    const isAssistant = role === 'assistant';
    const isSystem = role === 'system';
    const hasBlocks = blocks && blocks.length > 0;
    const complex = hasBlocks && hasComplexBlocks(blocks);

    const showAvatar = !!avatar && avatarPosition !== 'none';
    const squareTop = showAvatar && isAssistant && avatarPosition === 'top';
    const squareBottom = showAvatar && isAssistant && avatarPosition === 'bottom';
    // Default tail only when no explicit avatarPosition is set
    const showTail = isAssistant && !squareTop && !squareBottom;

    return (
      <div
        ref={ref}
        data-role={role}
        className={cn('flex gap-2', isUser ? 'flex-row-reverse' : 'flex-row', className)}
        {...props}
      >
        {/* Avatar — position controlled by avatarPosition prop */}
        {showAvatar && (
          <div
            className={cn(
              'shrink-0',
              squareBottom ? 'self-end' : 'self-start',
              squareTop && '-mt-3',
            )}
          >
            {avatar}
          </div>
        )}

        {/* Bubble + timestamp */}
        <div
          className={cn(
            'flex flex-col gap-1',
            isUser ? 'items-end' : 'items-start',
            complex ? 'w-full max-w-[90%]' : 'max-w-[72ch]',
          )}
        >
          {/* ── Bubble ── */}
          {loading ? (
            // Streaming indicator — always in a bubble
            <div
              className={cn(
                'rounded-2xl px-4 py-2.5 text-sm',
                isUser && 'bg-primary text-primary-foreground rounded-br-sm',
                isAssistant && 'bg-muted text-foreground',
                showTail && 'rounded-bl-sm',
                squareTop && 'rounded-tl-none',
                squareBottom && 'rounded-bl-none',
              )}
            >
              <StreamingDots />
            </div>
          ) : hasBlocks && isAssistant ? (
            // Assistant blocks: no wrapper — content rendered directly
            <div
              className={cn(
                'space-y-3 w-full',
                squareTop && 'rounded-tl-none',
                squareBottom && 'rounded-bl-none',
              )}
            >
              {blocks.map((block, i) => (
                <MessageBlockRenderer key={i} block={block} />
              ))}
            </div>
          ) : hasBlocks ? (
            // User blocks: bordered card
            <div
              className={cn(
                'border border-border bg-card rounded-2xl overflow-hidden text-sm',
                isUser && 'rounded-br-sm',
              )}
            >
              <div className="divide-y divide-border">
                {blocks.map((block, i) => (
                  <div key={i} className="px-4 py-3">
                    <MessageBlockRenderer block={block} />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            // Plain text — coloured bubble
            <div
              className={cn(
                'rounded-2xl px-4 py-2.5 text-sm',
                isUser && 'bg-primary text-primary-foreground rounded-br-sm',
                isAssistant && 'bg-muted text-foreground',
                isSystem && 'bg-muted/50 text-muted-foreground text-xs italic',
                showTail && 'rounded-bl-sm',
                squareTop && 'rounded-tl-none',
                squareBottom && 'rounded-bl-none',
              )}
            >
              {content}
            </div>
          )}

          {timestamp && <time className="text-xs text-muted-foreground px-1">{timestamp}</time>}
        </div>
      </div>
    );
  },
);
ChatMessage.displayName = 'ChatMessage';

export { ChatMessage };
